import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { Language } from '@/admin/languages';
import { LANGUAGE_LABELS, LANGUAGE_OPTIONS } from '@/admin/languages';
import { parseLanguage, tableSearchKeys } from '@/admin/sitecontent/listSearch';
import buttonStyles from '@/admin/styles/button.module.css';
import { Badge } from '@/admin/ui/Badge';
import { createTablePage } from '@/admin/ui/createTablePage';
import { Select } from '@/admin/ui/Form';
import { BackLink } from '@/admin/ui/NavLinks';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import i18n from '~/i18n';

type Item = components['schemas']['AdminHelpSectionItem'];

const columnHelper = createColumnHelper<Item>();

const columns = [
  columnHelper.accessor('id', { header: () => i18n.t('admin.common.id') }),
  columnHelper.accessor('name', { header: () => i18n.t('admin.common.name') }),
  columnHelper.accessor('language', {
    header: () => i18n.t('admin.common.language'),
    cell: (c) => i18n.t(LANGUAGE_LABELS[c.getValue()]),
  }),
  columnHelper.accessor('sort_order', {
    header: () => i18n.t('admin.common.order'),
    cell: (c) => c.getValue() ?? '—',
  }),
  columnHelper.accessor('article_count', {
    header: () => i18n.t('admin.help.articles'),
  }),
  columnHelper.accessor('placement_count', {
    header: () => i18n.t('admin.help.placementsColumn'),
  }),
  columnHelper.accessor('excluded_profile_types', {
    id: 'visibility',
    header: () => i18n.t('admin.help.visibility'),
    // NULL is "never configured": in no member's global help tree.
    cell: (c) =>
      c.getValue() === null ? (
        <Badge $variant="warning">{i18n.t('admin.help.notInGlobalHelp')}</Badge>
      ) : null,
  }),
];

function LanguageFilter() {
  const navigate = useNavigate();
  const { language } = useSearch({ strict: false }) as { language?: Language };
  return (
    <Bar>
      <Select
        aria-label={i18n.t('admin.common.language')}
        value={language ?? ''}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          void navigate({
            to: './',
            replace: true,
            search: ((prev: Record<string, unknown>) => ({
              ...prev,
              language: e.target.value || undefined,
              page: undefined,
            })) as never,
          })
        }
      >
        <option value="">{i18n.t('admin.help.allLanguages')}</option>
        {LANGUAGE_OPTIONS.map(([code, label]) => (
          <option key={code} value={code}>
            {i18n.t(label)}
          </option>
        ))}
      </Select>
      <Link to="/admin/help/sections/new/" className={buttonStyles.Button}>
        {i18n.t('admin.help.newSection')}
      </Link>
    </Bar>
  );
}

const SectionList = createTablePage<Item, '/admin/help/sections/$sectionId'>({
  name: 'admin.help.sections',
  urlState: true,
  useData: (_page, _search, { paging }) => {
    const { language } = useSearch({ strict: false }) as {
      language?: Language;
    };
    return api.listAdminHelpSections.useQuery({
      query: { ...paging, language },
    });
  },
  columns,
  itemBaseUrl: '/admin/help/sections/$sectionId',
  getParams: (row) => ({ sectionId: String(row.id) }),
  actions: <LanguageFilter />,
});

function HelpSections() {
  const { t } = useTranslation();
  return (
    <>
      <BackLink>
        <Link to="/admin/help/">{t('admin.help.backToArticles')}</Link>
      </BackLink>
      <SectionList />
    </>
  );
}

export const Route = createFileRoute('/admin/help/sections/')({
  staticData: { minTier: 'ADMIN' },
  validateSearch: (
    s: Record<string, unknown>,
  ): { language?: Language } & Record<string, unknown> => ({
    ...tableSearchKeys(s),
    language: parseLanguage(s.language),
  }),
  component: HelpSections,
});

const Bar = styled.div`
  align-items: center;
  display: flex;
  gap: 0.75rem;
`;
